import React, { useCallback, useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';

import { RocheUIButton, RocheUIButtonAppearance, RocheUIGrid, RocheUITextLink } from '@repo/roche-ui';
import { breakpoints } from '@rocheglobal/component-library-dot-com/src/style/base';
import { trackTextLink } from '../../helpers/roche-text-link-tracking';
import { ExternalDataService } from '../../services/external-data';
import { Pipeline, RochePipelineEndpoint } from '../../services/external-data/types';
import { FilterProps, FilterService } from '../../services/filter';
import { LanguageService } from '../../services/language';

export interface RocheSelectItem {
  label: string;
  value: string;
  group?: string;
  checked?: boolean;
}

export const cardiovascularOldLabel = 'Cardiovascular & Metabolism';
export const cardiovascularNewLabel = 'Cardiovascular, Renal & Metabolism';
const cardiovascularKey = 'cardiovascular & metabolism';

const translations = {
  oncology: 'Oncology',
  'oncology/hematology': 'Oncology/Hematology',
  immunology: 'Immunology',
  cardiovascular: cardiovascularNewLabel,
  'cardiovascular & metabolism': cardiovascularNewLabel,
  ophthalmology: 'Ophthalmology',
  'infectious-diseases': 'Infectious Diseases',
  neuroscience: 'Neuroscience',
  virology: 'Virology',
  other: 'Other',
  ai: 'Additional Indication',
  nme: 'New Molecular Entity',
  one: 'Phase 1',
  tow: 'Phase 2',
  three: 'Phase 3',
  four: 'Phase 4',
  chugai: 'chugai',
  'roche-late-stage-product-development': 'Roche Late Stage Product Development',
  'genentech-research-and-early-development': 'Genentech Research and Early Development',
  'pharma-research-and-early-development': 'Pharma Research and Early Development',
  spark: 'spark',
  'partner-managed': 'Partner Managed',
};

const groupListByOptions = [
  { label: 'All', value: 'all' },
  { label: 'Therapeutic Area', value: 'therapeuticAreaGroupByValue' },
  { label: 'Partner', value: 'partner' },
  { label: 'Phase', value: 'phase' },
  { label: 'Managed By', value: 'managedBy' },
  { label: 'Expected Filing', value: 'filingDate' },
];

export enum FilterGroupType {
  THERAPEUTIC_AREA = 'therapeuticArea',
  THERAPEUTIC_AREA_FILTER_VALUE = 'therapeuticAreaFilterValue',
  PROJECT_TYPE = 'projectType',
  PHASE = 'phase',
  EXPECTED_FILING_DATE = 'filingDate',
  MANAGED_BY = 'managedBy',
}

export enum ProjectType {
  NEW_MOLECULAR_ENTITY = 'nme',
  ADDITIONAL_INDICATOR = 'ai',
}

export enum TypeNames {
  SEARCH = 'search',
  GROUP_LIST_BY = 'groupListBy',
}

export enum Phases {
  PHASE_ONE = 'Phase 1',
  PHASE_TWO = 'Phase 2',
  PHASE_THREE = 'Phase 3',
  FILED = 'Filed',
  APPROVED = 'Approved',
}

interface FiltersOptions {
  therapeuticAreaFilterValue: RocheSelectItem[];
  projectTypes: RocheSelectItem[];
  phases: RocheSelectItem[];
  filingDates: RocheSelectItem[];
  managedBy: RocheSelectItem[];
}

// Helper for sorting/moving an item to the end
const moveEntryToEnd = (pipelineEntries: RocheSelectItem[], value: string): RocheSelectItem[] =>
  pipelineEntries.sort((item) => {
    if (item.value !== value) {
      return 0;
    }
    return 1;
  });

// Returns an array of RocheSelectItem with unique `key` values
const getOptionsByKey = (pipelineEntries: Pipeline[], key: keyof Pipeline): RocheSelectItem[] => {
  const names = pipelineEntries
    ?.map((entry: Pipeline) => entry[key])
    ?.filter((entry, index, self) => index === self.indexOf(entry));

  return names
    ?.map((name) => ({
      group: key,
      label: translations[name] ? translations[name] : name,
      value: name,
    }))
    ?.sort((left, right) => (left?.label?.toLowerCase() > right?.label?.toLowerCase() ? 1 : -1))
    ?.filter((element) => element?.label);
};

// We need to handle the "global-modal" if it exists in the DOM
const modal = typeof document !== 'undefined' ? document.getElementById('global-modal') : null;

function escapeCSVValue(value: string): string {
  return `"${value}"`;
}

// ------------------ Functional Component ------------------
export const RochePipeline = () => {
  const originalContent = useRef<Pipeline[]>([]);
  const containerRef = useRef<HTMLDivElement>(null);

  const isRightToLeft = LanguageService.isRightToLeft();

  const downloadButtonLabel = 'Download current view as CSV';

  // Local states
  const [content, setContent] = useState<Pipeline[]>([]);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [filters, setFilters] = useState<Map<string, FilterProps>>(new Map());
  const [searchText, setSearchText] = useState<string>('');
  const [groupListBy, setGroupListBy] = useState<string>('all');
  const [isMedium, setIsMedium] = useState<boolean>(false);
  const [isSmall, setIsSmall] = useState<boolean>(false);
  const [filterOptions, setFilterOptions] = useState<FiltersOptions>({
    therapeuticAreaFilterValue: [],
    projectTypes: [],
    phases: [],
    filingDates: [],
    managedBy: [],
  });

  const handleResize = useCallback(() => {
    const width = document.body.clientWidth;
    setIsSmall(width < breakpoints.medium);
    setIsMedium(width < breakpoints.large);
  }, []);

  // clean and modify incoming pipeline entries
  const cleanupPipelineEntry = useCallback((dirtyPipelineEntry): Pipeline => {
    if (dirtyPipelineEntry.visibility === false) {
      return null;
    }
    const newEntry = { ...dirtyPipelineEntry };
    newEntry.originalPhase = newEntry.phase;

    if (!newEntry.phase || newEntry.phase === '-') {
      newEntry.phase = '';
    }

    // If phase is Approved -> treat as Filed
    if (newEntry.phase === Phases.APPROVED) {
      newEntry.phase = Phases.FILED;
    }

    // Handle the cardiovascular naming
    if (newEntry.therapeuticArea === cardiovascularOldLabel || newEntry.therapeuticArea === cardiovascularNewLabel) {
      newEntry.therapeuticArea = cardiovascularKey;
      newEntry.therapeuticAreaFilterValue = cardiovascularKey;
      newEntry.therapeuticAreaGroupByValue = cardiovascularNewLabel;
    } else {
      newEntry.therapeuticAreaFilterValue = newEntry.therapeuticArea?.toLowerCase();
      newEntry.therapeuticAreaGroupByValue = newEntry.therapeuticArea;
    }

    return newEntry as Pipeline;
  }, []);

  useEffect(() => {
    const fetchPipelineData = async () => {
      try {
        const response: RochePipelineEndpoint<Pipeline> = await ExternalDataService.getPipeline();
        const cleaned = response.Items.map((item) => cleanupPipelineEntry(item)).filter(Boolean);
        originalContent.current = cleaned;
        setContent(cleaned);
        updateFilterOptions(cleaned);
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error fetching pipeline data: ', error);
      }
    };

    fetchPipelineData();
  }, [cleanupPipelineEntry]);

  // Add event listeners on mount, remove on unmount
  useEffect(() => {
    const currentContainer = containerRef.current;

    if (currentContainer) {
      currentContainer.addEventListener('rocheFormControlChange', handleFiltersUpdate);
    }
    if (modal) {
      modal.addEventListener('rocheFormControlChange', handleFiltersUpdate);
    }

    window.addEventListener('rocheModalTrigger', handleModalTrigger);
    window.addEventListener('rocheModalClose', handleModalClose);
    window.addEventListener('resize', handleResize);

    // Run once to set initial widths
    handleResize();

    // On unmount
    return () => {
      if (currentContainer) {
        currentContainer.removeEventListener('rocheFormControlChange', handleFiltersUpdate);
      }
      if (modal) {
        modal.removeEventListener('rocheFormControlChange', handleFiltersUpdate);
      }
      window.removeEventListener('rocheModalTrigger', handleModalTrigger);
      window.removeEventListener('rocheModalClose', handleModalClose);
      window.removeEventListener('resize', handleResize);
    };
  }, [handleResize]);

  const handleFiltersUpdate = useCallback((event: CustomEvent) => {
    const { name, value } = event?.detail;
    if (name === TypeNames.GROUP_LIST_BY) {
      setGroupListBy(value);
      return;
    }
    if (name === TypeNames.SEARCH) {
      setSearchText(value);
      return;
    }

    setFilters((prev) => {
      const newMap = new Map(prev);
      newMap.set(name, { attributeName: name, filter: value });
      return newMap;
    });
  }, []);

  useEffect(() => {
    // Filter content by search & by selected filters
    let newContent = FilterService.filterByAttributes(
      originalContent.current.map((item) => ({ ...item })),
      Array.from(filters.values()),
      ',',
    ) as Pipeline[];

    newContent = FilterService.filterBySearchText(newContent, searchText).filter(Boolean) as Pipeline[];

    setContent(newContent);
  }, [filters, searchText]);

  const handleModalTrigger = useCallback(() => {
    if (modal) {
      modal.setAttribute('appearance', 'pipeline');
    }
    setIsModalOpen(true);
  }, []);

  const handleModalClose = useCallback(() => {
    if (modal) {
      modal.setAttribute('appearance', 'default');
    }
    setIsModalOpen(false);
  }, []);

  const createAndDownloadCSV = useCallback(() => {
    const data = content;
    const header = [
      'Compound',
      'Generic Name',
      'Trade name',
      'Combination',
      'Therapeutic area',
      'Project type',
      'Indication',
      'Phase',
      'Filing date',
      'Partner',
      'Managed by',
      'Description',
    ];

    const csvRows = [
      header.join(','),
      ...data
        .map((item) => [
          escapeCSVValue(item.name),
          escapeCSVValue(item.genericName),
          escapeCSVValue(item.tradeName),
          escapeCSVValue(item.combination),
          // map to correct value, see EXTCWP-475
          escapeCSVValue(item.therapeuticArea === cardiovascularKey ? cardiovascularNewLabel : item.therapeuticArea),
          escapeCSVValue(item.projectType),
          escapeCSVValue(item.indicationShort),
          escapeCSVValue(item.originalPhase),
          escapeCSVValue(item.filingDate),
          escapeCSVValue(item.partner),
          escapeCSVValue(item.managedBy),
          escapeCSVValue(item.description),
        ])
        .map((row) => row.join(',')),
    ];

    const csvString = csvRows.join('\n');
    const dataURL = URL.createObjectURL(new Blob([csvString], { type: 'text/csv;charset=utf-8;' }));
    const temporaryLink = document.createElement('a');
    temporaryLink.href = dataURL;
    temporaryLink.setAttribute('download', `Roche_Pipeline_Final_${new Date().toISOString().split('T')[0]}.csv`);
    document.body.appendChild(temporaryLink);
    temporaryLink.click();
    document.body.removeChild(temporaryLink);
    URL.revokeObjectURL(dataURL);
  }, [content]);

  const handleDownloadButtonClick = (text: string) => {
    createAndDownloadCSV();
    trackTextLink({
      text,
    });
  };

  const updateFilterOptions = useCallback((pipelineEntries: Pipeline[]) => {
    const tAFilterValue = getOptionsByKey(pipelineEntries, FilterGroupType.THERAPEUTIC_AREA_FILTER_VALUE);
    const projectTypes = getOptionsByKey(pipelineEntries, FilterGroupType.PROJECT_TYPE);
    const phases = getOptionsByKey(pipelineEntries, FilterGroupType.PHASE);
    const filingDates = getOptionsByKey(pipelineEntries, FilterGroupType.EXPECTED_FILING_DATE);
    const managedBy = getOptionsByKey(pipelineEntries, FilterGroupType.MANAGED_BY);

    setFilterOptions({
      therapeuticAreaFilterValue: moveEntryToEnd(tAFilterValue, 'other'),
      projectTypes,
      phases: moveEntryToEnd(phases, Phases.FILED),
      filingDates,
      managedBy,
    });
  }, []);

  const getIcon = useCallback((value: RocheSelectItem): string | null => {
    if (value.group === FilterGroupType.THERAPEUTIC_AREA_FILTER_VALUE) {
      return 'color-indicator';
    }
    if (value.group === FilterGroupType.PROJECT_TYPE && value.value === ProjectType.ADDITIONAL_INDICATOR) {
      return 'additional';
    }
    if (value.group === FilterGroupType.PROJECT_TYPE && value.value === ProjectType.NEW_MOLECULAR_ENTITY) {
      return 'molecular-item';
    }
    return null;
  }, []);

  const getFilterItem = (headline: string, name: string, values: RocheSelectItem[]) => {
    const selected = filters.get(name)?.filter as string;

    const mappedValues = values.map((value: RocheSelectItem) => ({
      ...value,
      checked: selected?.indexOf(value.value) >= 0,
      icon: getIcon(value),
    }));

    return (
      <roche-accordion-tabs-item
        headline={headline}
        appearance='pipeline'
        is-overflow-y-scrollable={mappedValues.length > 3}
      >
        <roche-select
          label-wrapper
          select-all
          multi-select
          name={name}
          options={JSON.stringify(mappedValues)}
          enable-scroll-into-view
        />
      </roche-accordion-tabs-item>
    );
  };

  const getFiltersView = () => (
    <roche-accordion-tabs appearance='pipeline' type='accordion' no-margin>
      {getFilterItem('Therapeutic Area', 'therapeuticAreaFilterValue', filterOptions.therapeuticAreaFilterValue)}
      {getFilterItem('Project Type', 'projectType', filterOptions.projectTypes)}
      {getFilterItem('Phase', 'phase', filterOptions.phases)}
      {getFilterItem('Expected Filing Date', 'filingDate', filterOptions.filingDates)}
      {getFilterItem('Managed By', 'managedBy', filterOptions.managedBy)}
    </roche-accordion-tabs>
  );

  const getSearchInputField = () => (
    <roche-input name='search' type='search' label='Search' value={searchText} required />
  );

  const getGroupByDropdown = () => {
    const selectedIndex = groupListByOptions.findIndex((option) => option.value === groupListBy);
    return (
      <roche-dropdown
        name='groupListBy'
        label='Group list by'
        selected={selectedIndex}
        required
        options={JSON.stringify(groupListByOptions)}
      />
    );
  };

  return (
    <div ref={containerRef}>
      {isModalOpen &&
        modal &&
        ReactDOM.createPortal(
          <RocheUIGrid layout='12' fullWidth={false}>
            <div slot='full'>
              <roche-headline headline='Filter' level={2}></roche-headline>
              {getSearchInputField()}
              {getGroupByDropdown()}
              {getFiltersView()}
            </div>
          </RocheUIGrid>,
          modal,
        )}

      {isMedium && (
        <RocheUIGrid layout='12' fullWidth={true}>
          <div slot='full'>
            <roche-pipeline-table
              is-small={isSmall}
              group-list-by={groupListBy}
              content={JSON.stringify(content)}
            ></roche-pipeline-table>

            <RocheUIButton
              text='Filter'
              appearance={RocheUIButtonAppearance.BOTTOM_FIXED}
              isModalTrigger
              onModalTrigger={() => {
                window.dispatchEvent(new CustomEvent('rocheModalTrigger'));
              }}
              hasMarginTop={false}
              rightIcon={'filter'}
            ></RocheUIButton>
          </div>
        </RocheUIGrid>
      )}

      {!isMedium && (
        <RocheUIGrid layout='8-1-3' overflowVisible={true} fullWidth={true} isRightToLeft={isRightToLeft}>
          <roche-pipeline-table
            is-small={isSmall}
            slot={isRightToLeft ? 'right' : 'left'}
            group-list-by={groupListBy}
            content={JSON.stringify(content)}
          ></roche-pipeline-table>

          <div
            slot={isRightToLeft ? 'left' : 'right'}
            style={{
              position: 'sticky',
              top: '1rem',
              zIndex: 100,
            }}
          >
            <RocheUITextLink
              text={downloadButtonLabel}
              leftIcon='download'
              onButtonClick={() => handleDownloadButtonClick(downloadButtonLabel)}
            />
            {getSearchInputField()}
            {getGroupByDropdown()}
            {getFiltersView()}
          </div>
        </RocheUIGrid>
      )}
    </div>
  );
};
